import { GraduationCap, BarChart3, FileCheck2, ShieldCheck } from "lucide-react";
import { useBranding, resolveAssetUrl } from "@/contexts/BrandingContext";
import { BrandLogo } from "@/components/BrandLogo";

const ICONS = [GraduationCap, BarChart3, FileCheck2];

const Hero = () => {
  const { login = {}, logo_url, logo_dark_url, company_name } = useBranding();
  const logo = resolveAssetUrl(logo_dark_url || logo_url);
  const pillars = (login.pillars || "").split("|").map((p) => p.trim()).filter(Boolean);
  return (
    <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#06142e] p-12 xl:p-16 text-white" data-testid="auth-hero">
      {login.photo_url && <img src={login.photo_url} alt="" className="absolute inset-0 h-full w-full object-cover object-[70%_center] opacity-80 auth-hero-zoom" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#06142e] via-[#06142e]/70 to-[#0b2a5c]/10" />
      <div className="absolute -right-40 bottom-[-30%] h-[120%] w-[70%] rounded-[50%] border-t-2 border-sky-400/40 rotate-[-18deg]" />
      <div className="relative auth-rise">
        {logo ? <img src={logo} alt={company_name} className="h-24 w-auto object-contain" data-testid="auth-hero-logo" /> : <p className="font-heading text-2xl font-extrabold">{company_name}</p>}
        {pillars.length > 0 && <p className="mt-4 text-[11px] uppercase tracking-[0.45em] text-sky-100/80">{pillars.join("  |  ")}</p>}
      </div>
      <div className="relative max-w-xl auth-rise [animation-delay:120ms]">
        <h1 className="font-heading text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight" data-testid="auth-headline">
          {login.headline} <span className="text-sky-400">{login.highlight}</span>
        </h1>
        {login.subtext && <p className="mt-6 text-base text-slate-200/90 leading-relaxed">{login.subtext}</p>}
        <div className="mt-10 grid grid-cols-3 gap-6">
          {(login.features || []).slice(0, 3).map((f, i) => {
            const Icon = ICONS[i];
            return (
              <div key={i} data-testid={`auth-feature-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-sky-400/50 bg-sky-500/10 shadow-[0_0_24px_rgba(56,189,248,0.25)]"><Icon size={18} className="text-sky-300" /></span>
                <p className="mt-3 text-sm font-semibold leading-tight">{f.title}</p>
                <p className="mt-1 text-xs text-slate-300/80">{f.text}</p>
              </div>
            );
          })}
        </div>
      </div>
      {login.tagline && <p className="relative font-script text-4xl text-white/85 -rotate-6 origin-left auth-rise [animation-delay:240ms]" data-testid="auth-tagline">{login.tagline.split(/(?<=\.)\s+/).map((t, i) => <span key={i} className="block" style={{ marginLeft: i * 18 }}>{t}</span>)}</p>}
    </section>
  );
};

export const AuthShell = ({ cardLogo, portalLabel, title, subtitle, children, switchLink, testId }) => {
  const { company_name, support_email } = useBranding();
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-[#06142e]" data-testid={testId}>
      <Hero />
      <section className="relative flex items-center justify-center p-5 sm:p-10 bg-gradient-to-br from-[#0b2a5c] via-[#0a3a7a] to-[#06142e]">
        <div className="w-full max-w-[460px] rounded-2xl bg-white p-8 sm:p-10 shadow-2xl shadow-black/30 auth-rise [animation-delay:80ms]">
          <div className="flex flex-col items-center">
            {cardLogo || <BrandLogo size="2xl" />}
            {portalLabel && <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-blue-700" data-testid="auth-portal-label">{portalLabel}</p>}
          </div>
          <h2 className="mt-6 text-center font-heading text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
          {subtitle && <p className="mt-2 text-center text-sm text-slate-500">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
          <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4" data-testid="auth-secure-note">
            <ShieldCheck size={22} className="mt-0.5 shrink-0 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-slate-800">A secure and trusted learning environment.</p>
              <p className="text-xs text-slate-500">Your information is protected and managed by {company_name || "your training provider"}.</p>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">
            Having trouble signing in?{" "}
            {support_email ? <a href={`mailto:${support_email}`} className="text-blue-600 hover:underline" data-testid="auth-contact-admin">Contact your administrator.</a> : "Contact your administrator."}
          </p>
          {switchLink && <p className="mt-2 text-center text-xs text-slate-500" data-testid="auth-switch-portal">{switchLink}</p>}
        </div>
      </section>
    </div>
  );
};
